import { LucideCirclePlus, LucideX } from "lucide-react";
import { ChangeEvent } from "react";
import DatePicker from "react-datepicker";

interface OrgUser {
  _id: { $oid: string };
  firstName: string;
  lastName: string;
  employeeId: string;
  email: string;
  mobile: string;
  premise?: string | Array<{ $oid: string }> | string[];
}

interface FireDrillFormData {
  _id?: string;
  region: string;
  premise: string;
  premisesId: string;
  report_send_email?: string[];
  date_of_test_drills: string | Date | null;
  person_responsibility: string;
  closing_timeline: Date | string | null;
  date_of_evidence: string | Date | null;
  participation_rate: string | number;
  observation_identified_for_improvement: string;
  area: string;
  floor: string;
  users?: string[];
  actions: string;
  closure_evidence: string;
  average_drill: string | number;
  evacuation_trend: string | number;
  next_drill_scheduled: string | Date | null;
  evidence_closure_files: File[] | string[] | null;
  status?: "scheduled" | "completed";
  feedback_gap_analysis?: string;
}

interface PerformDrillSectionProps {
  formData: FireDrillFormData;
  errors: Record<string, string>;
  handleChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void;
  handleRemoveFile: (index: number) => void;
  uploading: boolean;
  uploadProgress: number;
  emails: string[];
  handleEmailChange: (index: number, value: string) => void;
  handleAddEmail: () => void;
  listOfUsersFromPremisesId: OrgUser[];
  handleRemoveEmail: (index: number) => void;
  setFormData: React.Dispatch<React.SetStateAction<FireDrillFormData>>;
  moduleName?: string;
}

export const PerformDrillSection: React.FC<PerformDrillSectionProps> = ({
  formData,
  errors,
  handleChange,
  handleRemoveFile,
  uploading,
  uploadProgress,
  emails,
  handleEmailChange,
  handleAddEmail,
  handleRemoveEmail,
  moduleName,
  setFormData,
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Date of evidence */}
      <div>
        <label className="block mb-1 font-medium">
          Date of evidence <span className="text-red-500">*</span>
        </label>
        <DatePicker
          selected={
            typeof formData.date_of_evidence === "string"
              ? (() => {
                  const [day, month, year] =
                    formData.date_of_evidence.split("/");
                  const parsed = new Date(`${month}/${day}/${year}`);
                  return isNaN(parsed.getTime()) ? null : parsed;
                })()
              : formData.date_of_evidence
          }
          onChange={(date: Date | null) =>
            setFormData({ ...formData, date_of_evidence: date })
          }
          placeholderText="DD/MM/YYYY"
          dateFormat="dd/MM/yyyy"
          minDate={
            typeof formData.date_of_test_drills === "string"
              ? (() => {
                  const [day, month, year] =
                    formData.date_of_test_drills.split("/");
                  const parsed = new Date(`${month}/${day}/${year}`);
                  return isNaN(parsed.getTime()) ? undefined : parsed;
                })()
              : formData.date_of_test_drills || undefined
          }
          wrapperClassName="datePicker"
          className={`w-full border-2 ${
            errors.date_of_evidence ? "border-red-500" : "border-gray-400"
          } rounded p-2 focus:outline-none focus:border-blue-500`}
        />
        {errors.date_of_evidence && (
          <p className="text-red-500 text-sm mt-1">{errors.date_of_evidence}</p>
        )}
      </div>
      {/* Participation rate */}
      <div>
        <label className="block mb-1 font-medium">
          Participation rate (%) <span className="text-red-500">*</span>
        </label>
        <input
          type="number"
          name="participation_rate"
          value={formData.participation_rate}
          onChange={handleChange}
          min="0"
          max="100"
          readOnly
          className={`w-full border-2 ${
            errors.participation_rate ? "border-red-500" : "border-gray-400"
          } rounded p-2 focus:outline-none focus:border-blue-500`}
        />
        {errors.participation_rate && (
          <p className="text-red-500 text-sm mt-1">
            {errors.participation_rate}
          </p>
        )}
      </div>

      {/* Average Drill */}
      <div>
        <label className="block mb-1 font-medium">
          Average Drill Duration (mins) <span className="text-red-500">*</span>
        </label>
        <input
          name="average_drill"
          value={formData.average_drill}
          onChange={handleChange}
          className={`w-full border-2 ${
            errors.average_drill ? "border-red-500" : "border-gray-400"
          } rounded p-2 focus:outline-none focus:border-blue-500`}
        />
        {errors.average_drill && (
          <p className="text-red-500 text-sm mt-1">{errors.average_drill}</p>
        )}
      </div>

      {/* Evacuation time drill */}
      <div>
        <label className="block mb-1 font-medium">
          Evacuation Time Trend (min) <span className="text-red-500">*</span>
        </label>
        <input
          name="evacuation_trend"
          value={formData.evacuation_trend}
          onChange={handleChange}
          className={`w-full border-2 ${
            errors.evacuation_trend ? "border-red-500" : "border-gray-400"
          } rounded p-2 focus:outline-none focus:border-blue-500`}
        />
        {errors.evacuation_trend && (
          <p className="text-red-500 text-sm mt-1">{errors.evacuation_trend}</p>
        )}
      </div>

      {/* Next Drill scheduled */}
      <div>
        <label className="block mb-1 font-medium">
          Next drill scheduled <span className="text-red-500">*</span>
        </label>
        <DatePicker
          selected={
            typeof formData.next_drill_scheduled === "string"
              ? (() => {
                  const [day, month, year] =
                    formData.next_drill_scheduled.split("/");
                  const parsed = new Date(`${month}/${day}/${year}`);
                  return isNaN(parsed.getTime()) ? null : parsed;
                })()
              : formData.next_drill_scheduled
          }
          wrapperClassName="datePicker"
          onChange={(date: Date | null) =>
            setFormData({ ...formData, next_drill_scheduled: date })
          }
          placeholderText="DD/MM/YYYY"
          dateFormat="dd/MM/yyyy"
          minDate={(() => {
            const parseDate = (
              val: string | Date | null | undefined
            ): Date | undefined => {
              if (!val) return undefined;
              if (val instanceof Date) return val;
              if (typeof val === "string") {
                const [day, month, year] = val.split("/");
                const parsed = new Date(`${month}/${day}/${year}`);
                return isNaN(parsed.getTime()) ? undefined : parsed;
              }
              return undefined;
            };
            const testDate = parseDate(formData.date_of_test_drills);
            const evidenceDate = parseDate(formData.date_of_evidence);
            if (testDate && evidenceDate) {
              return testDate > evidenceDate ? testDate : evidenceDate;
            }
            return testDate || evidenceDate || undefined;
          })()}
          className={`w-full border-2 ${
            errors.next_drill_scheduled ? "border-red-500" : "border-gray-400"
          } rounded p-2 focus:outline-none focus:border-blue-500`}
        />
        {errors.next_drill_scheduled && (
          <p className="text-red-500 text-sm mt-1">
            {errors.next_drill_scheduled}
          </p>
        )}
      </div>
      {/* Evidence of closure */}
      <div className="col-span-3">
        <label className="block mb-1 font-medium">
          Evidence of closure <span className="text-red-500">*</span>
        </label>
        <textarea
          name="closure_evidence"
          value={formData.closure_evidence}
          onChange={handleChange}
          className={`w-full border-2 ${
            errors.closure_evidence ? "border-red-500" : "border-gray-400"
          } rounded p-2 focus:outline-none focus:border-blue-500`}
        ></textarea>
        {errors.closure_evidence && (
          <p className="text-red-500 text-sm mt-1">{errors.closure_evidence}</p>
        )}
      </div>

      {/* Observation */}
      <div className="col-span-3">
        <label className="block mb-1 font-medium">
          Observation identified for improvement{" "}
          <span className="text-red-500">*</span>
        </label>
        <textarea
          name="observation_identified_for_improvement"
          value={formData.observation_identified_for_improvement}
          onChange={handleChange}
          className={`w-full border-2 ${
            errors.observation_identified_for_improvement
              ? "border-red-500"
              : "border-gray-400"
          } rounded p-2 focus:outline-none focus:border-blue-500`}
        ></textarea>
        {errors.observation_identified_for_improvement && (
          <p className="text-red-500 text-sm mt-1">
            {errors.observation_identified_for_improvement}
          </p>
        )}
      </div>

      {/* Feedback & Gap Analysis - Only for External Emergency Preparedness */}
{moduleName === "external_emergency_preparedness" && (
  <div className="col-span-3">
    <label className="block mb-1 font-medium">
      Feedback & Gap Analysis <span className="text-red-500">*</span>
    </label>
    <textarea
      name="feedback_gap_analysis"
      value={formData.feedback_gap_analysis || ""}
      onChange={handleChange}
      placeholder="Provide detailed feedback and gap analysis from the emergency preparedness drill..."
      rows={4}
      className={`w-full border-2 ${
        errors.feedback_gap_analysis
          ? "border-red-500"
          : "border-gray-400"
      } rounded p-2 focus:outline-none focus:border-blue-500`}
    />
    {errors.feedback_gap_analysis && (
      <p className="text-red-500 text-sm mt-1">
        {errors.feedback_gap_analysis}
      </p>
    )}
  </div>
)}

      {/* Actions */}
      <div className="col-span-3">
        <label className="block mb-1 font-medium">
          Action to be taken <span className="text-red-500">*</span>
        </label>
        <textarea
          name="actions"
          value={formData.actions}
          onChange={handleChange}
          className={`w-full border-2 ${
            errors.actions ? "border-red-500" : "border-gray-400"
          } rounded p-2 focus:outline-none focus:border-blue-500`}
        ></textarea>
        {errors.actions && (
          <p className="text-red-500 text-sm mt-1">{errors.actions}</p>
        )}
      </div>

      {/* Evidence attachments */}
      <div className="col-span-3 mt-4">
        <span className="flex justify-between">
          <label htmlFor="file-upload" className="block mb-1 text-black">
            Evidence of closure Attachments
          </label>
          <p className="text-sm text-gray-600 mb-2">
            Supported file formats:{" "}
            <span className="text-orange-600">
              DOC/DOCX, PDF, TXT, JPG, PNG
            </span>{" "}
            Max file size: <span className="text-orange-600">5 MB</span>
          </p>
        </span>
        <label
          htmlFor="file-upload"
          className="block border-2 border-dashed border-gray-400 p-6 text-center text-blue-700 cursor-pointer hover:bg-blue-50"
        >
          {formData.evidence_closure_files &&
          Array.isArray(formData.evidence_closure_files) &&
          formData.evidence_closure_files.length > 0 ? (
            <div className="space-y-2">
              {formData.evidence_closure_files.map((file, index) => (
                <div
                  key={index}
                  className="text-gray-800 bg-gray-100 px-3 py-1 rounded text-sm flex justify-between items-center"
                >
                  <span className="truncate w-3/4 text-left">
                    {typeof file === "string"
                      ? file.split("/").pop()
                      : file.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className="text-red-500 hover:text-red-600"
                  >
                    <LucideX size={16} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            "Drag & Drop Your files here Or Click"
          )}
        </label>
        <input
          id="file-upload"
          type="file"
          className="hidden"
          multiple
          accept=".doc,.docx,.pdf,.txt,.jpg,.jpeg,.png"
          name="evidence_closure_files"
          onChange={handleChange}
        />
        {uploading && (
          <div className="mt-2">
            <p>Uploading: {uploadProgress}%</p>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-blue-600 h-2.5 rounded-full"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Email recipients */}
      <div className="col-span-3 mt-4">
        <label className="block font-medium">Send report to</label>
        <div className="flex flex-wrap items-center gap-3">
          {/* Additional email inputs */}
          {emails.map((email, index) => (
            <div key={index} className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleEmailChange(index, e.target.value)}
                  placeholder="name@example.com"
                  className={`w-72 border-2 ${
                    errors[`email_${index}`]
                      ? "border-red-500"
                      : "border-gray-400"
                  } rounded-lg p-2 pl-3 pr-8 focus:outline-none focus:border-blue-500`}
                />
                {emails.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveEmail(index)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500"
                  >
                    <LucideX size={18} />
                  </button>
                )}
              </div>
              {index === emails.length - 1 && (
                <button
                  type="button"
                  onClick={handleAddEmail}
                  className="text-blue-500 hover:text-blue-700 p-2 rounded-full hover:bg-blue-50 transition-colors"
                  title="Add another recipient"
                >
                  <LucideCirclePlus size={20} />
                </button>
              )}
              {errors[`email_${index}`] && (
                <p className="text-red-500 text-sm">
                  {errors[`email_${index}`]}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
